import { Component, Mixins, Prop } from 'vue-property-decorator'
import _ from 'lodash'

import DataGetComponent from '@/tcw/components/DataGetComponent'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { TCWBadDataStructureError, TCWBaseError } from '@/plugins/TCWBaseError'
import {
  ApiErrorCode,
  ApiGeneralObjectArrayPayload,
  ApiGeneralPayload,
  ApiGeneralSinglePayload,
  CheckResponse
} from '@/tcw/api/types'

import Getsudo from '@/tcw/domain/Getsudo'
import { TcwAPIDate, TcwAPIID } from '@/tcw/domain/types'
import { ERR_CODE_WEB_API_NOT_FOUND } from '@/tcw/constant/ErrorCode'

@Component
export default class KintaiGetsudoFetch extends Mixins(DataGetComponent) {
  @Prop({ default: 400 })
  protected height!: number
  @Prop({ type: Boolean, default: false })
  protected editable!: boolean
  @Prop({ default: () => Getsudo.now() })
  protected getsudo!: Getsudo
  @Prop({ default: null })
  protected userId!: TcwAPIID

  protected kintaiList: ApiGeneralObjectArrayPayload = []
  protected summary: ApiGeneralSinglePayload = {}
  protected keisan: ApiGeneralSinglePayload = {}
  protected teishutsu: ApiGeneralSinglePayload = {}

  protected koutsuhiList: ApiGeneralObjectArrayPayload = []
  protected keisanzumiList: ApiGeneralObjectArrayPayload = []

  protected responseInitWebApi(response: ApiGeneralPayload): void {
    if (Array.isArray(response)) {
      throw new TCWBadDataStructureError()
    }
    if (response['kintaiList'] && Array.isArray(response['kintaiList'])) {
      this.kintaiList = response['kintaiList'] as ApiGeneralObjectArrayPayload
    } else {
      // データ構造が異なっていたらエラー
      const error = new TCWBadDataStructureError('勤怠データの構造が異なります')
      this.throwError(error)
    }

    if (response['summary']) {
      this.summary = response['summary'] as ApiGeneralSinglePayload
    }
    if (response['keisan']) {
      this.keisan = response['keisan'] as ApiGeneralSinglePayload
    }
    if (response['teishutsu']) {
      this.teishutsu = response['teishutsu'] as ApiGeneralSinglePayload
    }
  }
  get apiPath(): string {
    if (this.userId && this.userId !== this.loginUserId) {
      // 他ユーザの勤怠情報
      return ApiPath.buildPath(ApiPath.Paths.OtherKintaiMonth.get, {
        userId: this.userId,
        year: this.getsudo.yearValue,
        month: this.getsudo.monthValue
      })
    } else {
      return ApiPath.buildPath(ApiPath.Paths.KintaiMonth.get, {
        year: this.getsudo.yearValue,
        month: this.getsudo.monthValue
      })
    }
  }

  protected async afterProc(): Promise<void> {
    // 勤怠チェックモードの場合はチェックする
    if (this.$tcw.$store.basic.isKintaiCheckMode) {
      await this.doCheckProc()
    }
  }

  protected openEdit(targetDate: Date): void {
    this.$tcw.$store.basic.setTargetDate(targetDate)
    this.$router.push('/kintai/input')
  }
  protected openKoutsuhi(targetDate: Date): void {
    this.$tcw.$store.basic.setTargetDate(targetDate)
    this.$router.push('/koutsuhi/input')
  }
  protected outputJikan(jikan: string): string {
    let output = ''
    const num = Number.parseFloat(jikan)
    if (num) {
      const ji = num / 60
      const hun = num % 60
      output = `${ji > 0 ? ji.toFixed(0) + 'h' : ''}${hun}m`
    }
    return output
  }

  private async doCheckProc(): Promise<ApiErrorCode> {
    const checkResult = await this.execCheckKintai()
    this.setCheckModeByCheckResult(checkResult)
    return checkResult
  }

  /**
   * 勤怠の入力チェックを行う
   */
  private async execCheckKintai(): Promise<ApiErrorCode> {
    this.isLoading = true

    // APIリクエスト情報を作成
    const keyInfo = {
      year: this.currentGetsudo.yearValue,
      month: this.currentGetsudo.monthValue,
      userId: `${this.userId}`
    }
    const path = ApiPath.buildPath(ApiPath.Paths.KintaiCheck.other, keyInfo)

    try {
      // APIを実行してエラー情報付き勤怠データを取得する
      const response: CheckResponse = await this.getWebApi().call<CheckResponse>(path, 'GET')
      if (!_.isArray(response.payload)) {
        throw new TCWBadDataStructureError('サーバから取得した勤怠チェックデータが予期せぬ構造です')
      }
      this.kintaiList = response.payload as ApiGeneralObjectArrayPayload
      return response.resultCode
    } catch (e) {
      if (e instanceof TCWBaseError) {
        await this.errorProc(e)
      }
      throw e
    } finally {
      this.isLoading = false
    }
  }

  /**
   * チェック結果に応じてチェックモードの状態を設定する
   */
  private setCheckModeByCheckResult(resultCode: ApiErrorCode) {
    switch (resultCode) {
      case 'ERROR':
      case 'WARN':
        // チェックモードをONにする
        this.$tcw.$store.basic.setKintaiCheckMode(true)
        break
      case 'NONE':
        // チェックモードをOFFにする
        this.$tcw.$store.basic.setKintaiCheckMode(false)
        break
    }
  }

  protected async exportDayChienShoumei(targetDate: TcwAPIDate): Promise<void> {
    const date = this.du.convertStrToDate(targetDate)
    if (!date) {
      return
    }
    try {
      const path = ApiPath.buildPath(ApiPath.Paths.ExportChienShoumei.day, {
        userId: this.userId ? this.userId : '',
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      })
      const response = await this.getWebApi().callRaw<string>(
        path,
        'GET',
        {},
        { responseType: 'blob' }
      )

      let fileName = this.tcw.getDecodedFileNameFromHeader(response.headers)
      if (fileName === null) {
        fileName = '遅延証明書.zip'
      }
      this.tcw.execDownloadFile(fileName, response.data)
    } catch (e) {
      let title = '遅延証明書の取得に失敗しました'
      let message = ''
      if (e instanceof TCWBaseError) {
        const errorCode = e.getErrorCode()
        if (errorCode === ERR_CODE_WEB_API_NOT_FOUND) {
          title = '遅延証明書が登録されていません'
        } else {
          message = `[${e.getErrorCode()}]${e.outputMessage()}`
        }
      } else {
        message = '不明なエラー'
      }
      this.$tcw.message({
        type: 'error',
        title,
        message
      })
    }
  }
}
